/**
 * Plans v2 card 3.5: the catalogue the upgrade modal shows. `GET /plans`
 * replaces the fallback below wholesale when it answers; the fallback only has
 * to tell the truth while the API is unreachable, in the backend's exact words
 * (`BackendPheonix.Plans.PublicCatalog`, asserted word for word by the
 * landing tests too).
 */

export const CATALOG_PLAN_IDS = new Set(['free', 'basic', 'pro', 'team', 'enterprise']);

// The same amounts in USD and EUR. The annual price is a fixed amount of its
// own (owner, 17.10.2026: 99.99 / 199.99 / 249.99 per seat), NOT 10 × monthly.
const usdEur = (cents, annualCents) => ({
  usd: { monthly_cents: cents, annual_cents: annualCents, label: `$${(cents / 100).toFixed(2)}` },
  eur: { monthly_cents: cents, annual_cents: annualCents, label: `€${(cents / 100).toFixed(2)}` },
});

export const DEFAULT_PLANS = [
  {
    id: 'free',
    name: 'FREE',
    price: '$0',
    priceSub: 'free',
    description: 'A preview of Apiboo on your own machine.',
    features: [
      'Try every tool on your own machine',
      '1 board, 5 documents, 2 collections, 20 requests',
      'One workspace: your Personal one',
      'Stays on this device: no cloud sync',
      'Free account required',
    ],
    prices: {
      usd: { monthly_cents: 0, annual_cents: null, label: '$0' },
      eur: { monthly_cents: 0, annual_cents: null, label: '€0' },
    },
    limits: { ai_tokens_per_month: 10000 },
  },
  {
    id: 'basic',
    name: 'BASIC',
    price: '€9.99',
    priceSub: 'per month',
    description: 'Unlimited local work on your own machine.',
    features: [
      'Everything in Free, without the limits',
      'Unlimited boards, documents, collections & requests',
      'Stays on this device: no cloud sync',
    ],
    prices: usdEur(999, 9999),
    limits: { ai_tokens_per_month: 50000 },
  },
  {
    id: 'pro',
    name: 'PRO',
    price: '€19.99',
    priceSub: 'per month',
    description: 'Cloud sync or local, your choice for every item.',
    highlight: true,
    features: [
      'Everything in Basic',
      'Unlimited sync across your devices',
      'Choose cloud or this device for every workspace',
      '25 GB file storage, 25 MB per file',
      'Cloud mock servers',
      'Publish to the API network',
    ],
    prices: usdEur(1999, 19999),
    limits: { ai_tokens_per_month: 500000 },
  },
  {
    id: 'team',
    name: 'TEAM',
    price: '€24.99',
    priceSub: 'per seat / mo',
    description: 'Per seat, for teams that build together.',
    features: [
      'Everything in Pro',
      'Shared team workspaces',
      '100 GB file storage per person, 25 MB per file',
      'Live presence & chat',
      'Roles & permissions',
      'Organisations & central billing',
    ],
    prices: usdEur(2499, 24999),
    limits: { ai_tokens_per_month: 1000000 },
  },
  {
    id: 'enterprise',
    name: 'CUSTOM ENTERPRISE',
    price: 'Custom',
    priceSub: '',
    description: 'For security, compliance and procurement needs.',
    features: [
      'SSO / SAML',
      'Audit logs',
      'Priority support',
      'Custom contracts',
      'Self-hosted deployment',
      '1 TB+ file storage, 50 MB per file',
      'All features included',
    ],
  },
];

export const CURRENCIES = ['usd', 'eur'];

/**
 * The Apiboo AI line on a plan card (owner, 16.10.2026: hosted Apiboo AI and
 * bring-your-own-key both stay). The number is the catalogue's
 * `limits.ai_tokens_per_month` (`GET /plans`, `Plans.serialize_limits`);
 * `DEFAULT_PLANS` above only mirrors the backend's fallback (Free 10,000,
 * Basic 50,000, Pro 500,000, Team 1,000,000 per seat) for when the API is
 * unreachable. A server `ai_tokens_label` wins. No number (null = unlimited,
 * Enterprise is custom) → no line rather than a guess.
 */
export function aiAllowanceLabel(plan) {
  const own = plan?.aiTokensLabel ?? plan?.ai_tokens_label;
  if (typeof own === 'string' && own.trim()) return own.trim();
  const n = plan?.limits?.ai_tokens_per_month;
  if (!Number.isInteger(n) || n <= 0) return '';
  const amount = n.toLocaleString('en-US');
  const perSeat = plan?.id === 'team' ? ' per seat' : '';
  return `Apiboo AI: ${amount} tokens a month${perSeat}, or bring your own key`;
}

/** EUR for a European time zone, USD otherwise. The user can switch in the modal. */
export function preferredCurrency(timeZone) {
  let tz = timeZone;
  if (tz === undefined) {
    try {
      tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      tz = '';
    }
  }
  return String(tz || '').startsWith('Europe/') ? 'eur' : 'usd';
}

const SYMBOL = { usd: '$', eur: '€' };

function money(currency, cents) {
  const amount = cents / 100;
  const text = Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  return `${SYMBOL[currency] || ''}${text}`;
}

const EN_SUBS = {
  'plans.billing.perYear': 'per year, billed annually',
  'plans.billing.perSeatYear': 'per seat / yr, billed annually',
};
const enOnly = (key) => EN_SUBS[key] ?? key;

/**
 * `{ price, sub }` for a plan card. Uses the server's `prices[currency]`
 * (Plans v2) and falls back to the old single `price` / `priceSub` labels a
 * pre-Plans-v2 backend sends. `t` (react-i18next) translates the annual sub;
 * without it the English one is used.
 */
export function priceDisplay(plan, cycle, currency, t = enOnly) {
  const p = plan?.prices?.[currency];
  if (!p) return { price: plan?.price ?? '', sub: plan?.priceSub ?? '' };
  if (cycle === 'annual' && Number.isInteger(p.annual_cents) && p.annual_cents > 0) {
    const sub = t(plan.id === 'team' ? 'plans.billing.perSeatYear' : 'plans.billing.perYear');
    return { price: money(currency, p.annual_cents), sub };
  }
  return { price: p.label || money(currency, p.monthly_cents || 0), sub: plan.priceSub ?? '' };
}

/**
 * How much cheaper a year is than twelve months, in whole percent ROUNDED
 * DOWN: 1 − annual / (12 × monthly), in integer arithmetic (16.59 % → 16).
 * null when there is nothing to show: no amount, a free plan, or an annual
 * price not below twelve months. The same rule as apiboo.com/pricing.
 */
export function annualSavingPercent(monthlyCents, annualCents) {
  if (!Number.isInteger(monthlyCents) || monthlyCents <= 0) return null;
  if (!Number.isInteger(annualCents) || annualCents < 0) return null;
  const twelve = 12 * monthlyCents;
  const pct = Math.floor((100 * (twelve - annualCents)) / twelve);
  return pct > 0 ? pct : null;
}

/**
 * Whether `plan` can be bought annually in `currency`: it needs BOTH the
 * annual Stripe price id (`stripe_price_annual_id` from GET /plans) and an
 * annual amount to show. Otherwise the card shows monthly even with Annual
 * selected, so the figure on the card is always the price checkout charges.
 */
export function offersAnnual(plan, currency) {
  const cents = plan?.prices?.[currency]?.annual_cents;
  return !!plan?.stripePriceAnnual && Number.isInteger(cents) && cents > 0;
}

/** The cycle a card actually sells: annual only where the plan offers it. */
export function effectiveCycle(plan, cycle, currency) {
  return cycle === 'annual' && offersAnnual(plan, currency) ? 'annual' : 'monthly';
}

/** The Stripe price id checkout gets for `plan` in `cycle` — never the other cycle's. */
export function stripePriceIdForPlan(plan, cycle) {
  return (cycle === 'annual' ? plan?.stripePriceAnnual : plan?.stripePriceMonthly) || null;
}

/** `annualSavingPercent` for one plan in one currency. */
export function planSaving(plan, currency) {
  const p = plan?.prices?.[currency];
  return p ? annualSavingPercent(p.monthly_cents, p.annual_cents) : null;
}

/** The largest saving among the plans that offer annual billing (the toggle's "Save up to N%"). */
export function maxSaving(plans, currency) {
  const all = (plans || [])
    .filter((p) => offersAnnual(p, currency))
    .map((p) => planSaving(p, currency))
    .filter((n) => n !== null);
  return all.length ? Math.max(...all) : null;
}

/** The annual price per month (annual / 12, to the cent), e.g. "$8.33" — or null. */
export function annualPerMonth(plan, currency) {
  const cents = plan?.prices?.[currency]?.annual_cents;
  if (!Number.isInteger(cents) || cents <= 0) return null;
  return money(currency, Math.round(cents / 12));
}
